import { useCallback, useEffect, useRef, useState } from 'react';
import { currentState, startLoading, subscribe } from '../utils/loader';
import './Loader.css';

/** Ring spinner. size in px. */
export function Spinner({ size = 40, className = '' }) {
  return (
    <span
      className={`app-spinner ${className}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <span className="app-spinner__ring" />
      <span className="app-spinner__core" />
    </span>
  );
}

/** Inline loader for a page or panel that is still fetching its data. */
export function PageLoader({ label = 'Loading…', compact = false, className = '' }) {
  return (
    <div className={`page-loader${compact ? ' page-loader--compact' : ''} ${className}`} role="status" aria-live="polite">
      <Spinner size={compact ? 22 : 40} />
      <span className="page-loader__label">{label}</span>
    </div>
  );
}

/** Full-screen backdrop with a spinner card. */
export function LoadingOverlay({ message = 'Please wait…', detail = '', slow = false }) {
  return (
    <div className="loading-overlay" role="alertdialog" aria-busy="true" aria-live="assertive" aria-label={message}>
      <div className="loading-overlay__card">
        <Spinner size={56} />
        <p className="loading-overlay__message">{message}</p>
        {detail ? <p className="loading-overlay__detail">{detail}</p> : null}
        <div className="loading-overlay__bar">
          <span />
        </div>
        {slow ? <p className="loading-overlay__detail">Still working — large files can take a minute. Please keep this tab open.</p> : null}
      </div>
    </div>
  );
}

/**
 * useState(false) look-alike for "busy" flags: setting it to true also shows
 * the backdrop with the message, setting it to false hides it.
 *   const [exporting, setExporting] = useBusyLoader('Preparing Excel…');
 */
export function useBusyLoader(message) {
  const [busy, setBusyState] = useState(false);
  const task = useRef(null);
  const setBusy = useCallback(
    (value) => {
      if (value && !task.current) task.current = startLoading(message);
      if (!value && task.current) {
        task.current.done();
        task.current = null;
      }
      setBusyState(Boolean(value));
    },
    [message],
  );
  useEffect(() => () => task.current?.done(), []);
  return [busy, setBusy];
}

const SHOW_AFTER_MS = 250; // quick saves finish without a flash
const SLOW_AFTER_MS = 6000;

/** Mounted once in App: shows the overlay while any loader task runs. */
export function GlobalLoader() {
  const [state, setState] = useState(currentState);
  const [visible, setVisible] = useState(false);
  const [slow, setSlow] = useState(false);

  useEffect(() => subscribe(setState), []);

  useEffect(() => {
    if (!state.active) {
      setVisible(false);
      setSlow(false);
      return undefined;
    }
    const elapsed = Date.now() - state.startedAt;
    const showTimer = setTimeout(() => setVisible(true), Math.max(0, SHOW_AFTER_MS - elapsed));
    const slowTimer = setTimeout(() => setSlow(true), Math.max(0, SLOW_AFTER_MS - elapsed));
    return () => {
      clearTimeout(showTimer);
      clearTimeout(slowTimer);
    };
  }, [state.active, state.startedAt]);

  useEffect(() => {
    if (!visible) return undefined;
    // Keep keyboard focus from reaching the page behind the backdrop.
    const previous = document.activeElement;
    if (previous && typeof previous.blur === 'function') previous.blur();
    const block = (event) => {
      if (event.key === 'Tab' || event.key === 'Enter' || event.key === ' ') event.preventDefault();
    };
    document.addEventListener('keydown', block, true);
    document.body.classList.add('loading-overlay-open');
    return () => {
      document.removeEventListener('keydown', block, true);
      document.body.classList.remove('loading-overlay-open');
    };
  }, [visible]);

  if (!visible || !state.active) return null;
  return <LoadingOverlay message={state.message} detail={state.detail} slow={slow} />;
}

export default GlobalLoader;
